import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon
} from '@mui/icons-material';

const RolesList = ({
  roles,
  allPermissions,
  onCreateRole,
  onUpdateRole,
  onUpdateRolePermissions,
  onDeleteRole,
  canManage
}) => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Group permissions by resource
  const permissionsByResource = allPermissions.reduce((acc, perm) => {
    const resource = perm.resource;
    if (!acc[resource]) {
      acc[resource] = [];
    }
    acc[resource].push(perm);
    return acc;
  }, {});

  const handleCreateClick = () => {
    setFormData({ name: '', description: '' });
    setSelectedPermissions([]);
    setCreateDialogOpen(true);
  };

  const handleEditClick = (role) => {
    setSelectedRole(role);
    setFormData({ name: role.name, description: role.description || '' });
    setEditDialogOpen(true);
  };

  const handlePermissionsClick = (role) => {
    setSelectedRole(role);
    setSelectedPermissions(role.permissions?.map(p => p.code) || []);
    setPermissionsDialogOpen(true);
  };

  const handleDeleteClick = (role) => {
    setSelectedRole(role);
    setDeleteDialogOpen(true);
  };

  const handleCreateSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Role name is required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await onCreateRole(formData.name, formData.description, selectedPermissions);
      setCreateDialogOpen(false);
      setFormData({ name: '', description: '' });
      setSelectedPermissions([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Role name is required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await onUpdateRole(selectedRole.id, formData.name, formData.description);
      setEditDialogOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionsSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      await onUpdateRolePermissions(selectedRole.id, selectedPermissions);
      setPermissionsDialogOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      await onDeleteRole(selectedRole.id);
      setDeleteDialogOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = (permCode) => {
    setSelectedPermissions((prev) =>
      prev.includes(permCode)
        ? prev.filter((p) => p !== permCode)
        : [...prev, permCode]
    );
  };

  const handleSelectAllInResource = (resource, perms) => {
    const permCodes = perms.map(p => p.code);
    const allSelected = permCodes.every(code => selectedPermissions.includes(code));
    if (allSelected) {
      setSelectedPermissions(prev => prev.filter(p => !permCodes.includes(p)));
    } else {
      setSelectedPermissions(prev => [...new Set([...prev, ...permCodes])]);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Roles
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage organization roles and their permissions
          </Typography>
        </Box>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
          >
            Create Role
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {roles.map((role) => (
          <Grid item xs={12} sm={6} md={4} key={role.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <SecurityIcon color="primary" />
                  <Typography variant="h6" component="div">
                    {role.name}
                  </Typography>
                  {role.isSystemRole && (
                    <Chip label="System" size="small" color="primary" variant="outlined" />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {role.description || 'No description'}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Permissions ({role.permissions?.length || 0})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {role.permissions?.slice(0, 5).map((perm) => (
                    <Chip
                      key={perm.code}
                      label={perm.code}
                      size="small"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  ))}
                  {(role.permissions?.length || 0) > 5 && (
                    <Chip
                      label={`+${role.permissions.length - 5} more`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              </CardContent>
              {canManage && (
                <CardActions>
                  <IconButton
                    size="small"
                    onClick={() => handlePermissionsClick(role)}
                    title="Edit Permissions"
                    disabled={role.isSystemRole}
                  >
                    <SecurityIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleEditClick(role)}
                    title="Edit Role"
                    disabled={role.isSystemRole}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteClick(role)}
                    title="Delete Role"
                    disabled={role.isSystemRole}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              )}
            </Card>
          </Grid>
        ))}
        {roles.length === 0 && (
          <Grid item xs={12}>
            <Alert severity="info">
              No roles found. Create your first custom role to get started.
            </Alert>
          </Grid>
        )}
      </Grid>

      {/* Create Role Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Role</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Role Name"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            sx={{ mb: 3 }}
          />
          <Typography variant="subtitle1" gutterBottom>
            Permissions
          </Typography>
          {Object.entries(permissionsByResource).map(([resource, perms]) => (
            <Accordion key={resource} defaultExpanded={false}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Typography sx={{ textTransform: 'capitalize' }}>{resource}</Typography>
                  <Chip
                    label={`${perms.filter(p => selectedPermissions.includes(p.code)).length}/${perms.length}`}
                    size="small"
                    color={perms.every(p => selectedPermissions.includes(p.code)) ? 'primary' : 'default'}
                  />
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectAllInResource(resource, perms);
                    }}
                  >
                    Toggle All
                  </Button>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <FormGroup>
                  {perms.map((perm) => (
                    <FormControlLabel
                      key={perm.code}
                      control={
                        <Checkbox
                          checked={selectedPermissions.includes(perm.code)}
                          onChange={() => handlePermissionToggle(perm.code)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">{perm.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {perm.code} - {perm.description}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>
              </AccordionDetails>
            </Accordion>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateSubmit} variant="contained" disabled={loading}>
            Create Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Role</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Role Name"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained" disabled={loading}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Permissions Dialog */}
      <Dialog
        open={permissionsDialogOpen}
        onClose={() => setPermissionsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Permissions for {selectedRole?.name}</DialogTitle>
        <DialogContent>
          {Object.entries(permissionsByResource).map(([resource, perms]) => (
            <Accordion key={resource} defaultExpanded={false}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Typography sx={{ textTransform: 'capitalize' }}>{resource}</Typography>
                  <Chip
                    label={`${perms.filter(p => selectedPermissions.includes(p.code)).length}/${perms.length}`}
                    size="small"
                    color={perms.every(p => selectedPermissions.includes(p.code)) ? 'primary' : 'default'}
                  />
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectAllInResource(resource, perms);
                    }}
                  >
                    Toggle All
                  </Button>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <FormGroup>
                  {perms.map((perm) => (
                    <FormControlLabel
                      key={perm.code}
                      control={
                        <Checkbox
                          checked={selectedPermissions.includes(perm.code)}
                          onChange={() => handlePermissionToggle(perm.code)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">{perm.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {perm.code} - {perm.description}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>
              </AccordionDetails>
            </Accordion>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermissionsDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePermissionsSubmit} variant="contained" disabled={loading}>
            Save Permissions
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Role Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
      >
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete the role "{selectedRole?.name}"?
            This action cannot be undone.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            All users with this role will lose the permissions it grants.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteSubmit} color="error" variant="contained" disabled={loading}>
            Delete Role
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesList;
